package dev.lingvo.shared

import kotlinx.serialization.Serializable

@Serializable
enum class ImageFormat(val mime: String, val extension: String) {
    PNG("image/png", "png"),
    JPEG("image/jpeg", "jpg"),
}

/** What a file's own headers say about it. Nothing here comes from decoding pixels. */
@Serializable
data class ImageHeader(
    val format: ImageFormat,
    val width: Int,
    val height: Int,
    val hasAlpha: Boolean,
    val bitDepth: Int,
    /** 1 grey or palette, 2 grey+alpha, 3 RGB/YCbCr, 4 RGBA. */
    val channels: Int,
    val interlacedOrProgressive: Boolean,
    /** PNG colour type 3: one index per pixel into a palette of RGB entries. */
    val palette: Boolean = false,
)

@Serializable
enum class ShotIssueCode(val code: String, val httpStatus: Int) {
    TOO_LARGE("screenshot_too_large", 413),
    UNSUPPORTED_TYPE("screenshot_unsupported_type", 415),
    MALFORMED("screenshot_malformed", 422),
    TRUNCATED("screenshot_truncated", 422),
    TRAILING_DATA("screenshot_trailing_data", 422),
    DIMENSIONS_OUT_OF_RANGE("screenshot_dimensions_out_of_range", 422),
    COLOR_UNSUPPORTED("screenshot_color_unsupported", 422),
    ANIMATED("screenshot_animated", 422),
    ALPHA_NOT_ALLOWED("screenshot_alpha_not_allowed", 422),
    WRONG_SIZE("screenshot_wrong_size", 422),
}

@Serializable
data class ShotIssue(val code: ShotIssueCode, val message: String, val wanted: List<PixelSize> = emptyList())

/** A run of bytes inside a file: a metadata segment the server strips before storing. */
@Serializable
data class ByteSpan(val offset: Long, val length: Long)

sealed interface ImageHeaderResult {
    /** [metadata]: JPEG EXIF/XMP/IPTC/COM/JUMBF/JFXX and PNG EXIF/text/caBX chunks, in file order, including between scans. */
    data class Ok(val header: ImageHeader, val metadata: List<ByteSpan> = emptyList()) : ImageHeaderResult
    data class Refused(val issue: ShotIssue) : ImageHeaderResult
}

/**
 * Random access to a file's bytes, so the same parser reads a `ByteArray` in the browser and CLI
 * and a file on the server's disk without holding it in memory.
 */
interface ImageBytes {
    val size: Long

    /** The byte at [offset] as 0..255. The parser checks every offset against [size] first. */
    fun byteAt(offset: Long): Int
}

class ByteArrayImageBytes(private val bytes: ByteArray) : ImageBytes {
    override val size: Long get() = bytes.size.toLong()
    override fun byteAt(offset: Long): Int = bytes[offset.toInt()].toInt() and 0xFF
}

/**
 * Header-only validation of PNG and JPEG, written for hostile input.
 *
 * Never allocates from a length field; every offset is `Long` arithmetic checked against the size
 * before it is read; every loop advances. Width and height are read unsigned (a PNG width of
 * 0x80000000 is 2,147,483,648, not a negative number that passes a `> max` check). JPEG markers are
 * walked without resynchronising on stray bytes outside entropy-coded scans, because a resync lets a
 * crafted file hide a fake start-of-frame behind garbage. Only IHDR's CRC is checked: the rest would
 * mean reading every byte twice for no safety gain, since nothing here decodes the image data.
 *
 * JPEG scan bytes are walked using marker escaping, without decoding pixels. Stuffed FF bytes and
 * restart markers remain image data; length-delimited segments between scans are parsed normally.
 * Trailing data after a JPEG's EOI or a PNG's IEND is refused (polyglot files).
 */
object ImageHeaders {
    const val MAX_PNG_CHUNKS = 100_000
    const val MAX_JPEG_SEGMENTS = 1_024

    private val PNG_SIGNATURE = intArrayOf(0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A)

    fun parse(bytes: ByteArray): ImageHeaderResult = parse(ByteArrayImageBytes(bytes))

    fun parse(bytes: ImageBytes): ImageHeaderResult = when {
        bytes.size >= 8 && PNG_SIGNATURE.indices.all { bytes.byteAt(it.toLong()) == PNG_SIGNATURE[it] } -> parsePng(bytes)
        bytes.size >= 3 && bytes.byteAt(0) == 0xFF && bytes.byteAt(1) == 0xD8 && bytes.byteAt(2) == 0xFF -> parseJpeg(bytes)
        else -> refused(ShotIssueCode.UNSUPPORTED_TYPE, "Screenshots must be PNG or JPEG. ${sniff(bytes)}")
    }

    // --- PNG -------------------------------------------------------------------------------------

    private fun parsePng(b: ImageBytes): ImageHeaderResult {
        if (b.size < 33) return refused(ShotIssueCode.TRUNCATED, "This PNG ends before its header does.")
        if (u32(b, 8) != 13L || chunkType(b, 12) != "IHDR") {
            return refused(ShotIssueCode.MALFORMED, "This PNG does not start with an IHDR header.")
        }
        val width = u32(b, 16)
        val height = u32(b, 20)
        if (width == 0L || height == 0L) return refused(ShotIssueCode.MALFORMED, "This PNG says it is ${width}×$height pixels.")
        dimensionIssue(width, height)?.let { return ImageHeaderResult.Refused(it) }
        val depth = b.byteAt(24)
        val colour = b.byteAt(25)
        val validDepths = when (colour) {
            0 -> setOf(1, 2, 4, 8, 16)
            2, 4, 6 -> setOf(8, 16)
            3 -> setOf(1, 2, 4, 8)
            else -> emptySet()
        }
        if (depth !in validDepths || b.byteAt(26) != 0 || b.byteAt(27) != 0 || b.byteAt(28) > 1) {
            return refused(ShotIssueCode.MALFORMED, "This PNG's header names an impossible pixel format.")
        }
        if (Crc32.of(b, 12, 17) != u32(b, 29)) return refused(ShotIssueCode.MALFORMED, "This PNG's header checksum is wrong.")

        var offset = 33L
        var chunks = 1
        var seenData = false
        var seenPalette = false
        var transparency = false
        val metadata = mutableListOf<ByteSpan>()
        while (true) {
            if (offset + 12 > b.size) return refused(ShotIssueCode.TRUNCATED, "This PNG is cut off: it has no IEND chunk.")
            val length = u32(b, offset)
            if (length > Int.MAX_VALUE) return refused(ShotIssueCode.MALFORMED, "This PNG has a chunk longer than any file can be.")
            val end = offset + 12 + length
            if (end > b.size) return refused(ShotIssueCode.TRUNCATED, "This PNG is cut off in the middle of a chunk.")
            chunks++
            if (chunks > MAX_PNG_CHUNKS) return refused(ShotIssueCode.MALFORMED, "This PNG has more than $MAX_PNG_CHUNKS chunks.")
            val type = chunkType(b, offset + 4)
                ?: return refused(ShotIssueCode.MALFORMED, "This PNG has a chunk with an invalid name.")
            when (type) {
                "IHDR" -> return refused(ShotIssueCode.MALFORMED, "This PNG has two IHDR headers.")
                "PLTE" -> {
                    if (seenData || seenPalette || length == 0L || length % 3 != 0L || length > 768L ||
                        (colour == 3 && length / 3 > (1 shl depth))
                    ) return refused(ShotIssueCode.MALFORMED, "This PNG has an invalid or misplaced palette.")
                    seenPalette = true
                }
                "IDAT" -> {
                    if (colour == 3 && !seenPalette) return refused(ShotIssueCode.MALFORMED, "This indexed PNG has no palette before its image data.")
                    seenData = true
                }
                "acTL" -> if (!seenData) return refused(ShotIssueCode.ANIMATED, "This is an animated PNG. Screenshots must be still images.")
                "tRNS" -> if (!seenData) transparency = true
                "eXIf", "tEXt", "zTXt", "iTXt", "caBX" -> metadata += ByteSpan(offset, end - offset)
                "IEND" -> {
                    if (!seenData) return refused(ShotIssueCode.MALFORMED, "This PNG has no image data.")
                    if (end != b.size) {
                        return refused(ShotIssueCode.TRAILING_DATA, "This PNG has ${b.size - end} bytes after its end. Re-export it.")
                    }
                    break
                }
            }
            offset = end
        }
        if (colour == 0 || colour == 4 || depth == 16) {
            return refused(ShotIssueCode.COLOR_UNSUPPORTED, "This PNG is ${if (depth == 16) "16-bit" else "greyscale"}. Save it as 8-bit RGB.")
        }
        val header = ImageHeader(
            format = ImageFormat.PNG,
            width = width.toInt(),
            height = height.toInt(),
            hasAlpha = colour == 6 || transparency,
            bitDepth = depth,
            channels = when (colour) { 2 -> 3; 6 -> 4; else -> 1 },
            interlacedOrProgressive = b.byteAt(28) == 1,
            palette = colour == 3,
        )
        return ImageHeaderResult.Ok(header, metadata)
    }

    /** Four ASCII letters, or null. */
    private fun chunkType(b: ImageBytes, offset: Long): String? {
        val chars = CharArray(4)
        for (i in 0 until 4) {
            val c = b.byteAt(offset + i)
            if (c !in 0x41..0x5A && c !in 0x61..0x7A) return null
            chars[i] = c.toChar()
        }
        return chars.concatToString()
    }

    // --- JPEG ------------------------------------------------------------------------------------

    private val SOF_ACCEPTED = setOf(0xC0, 0xC1, 0xC2)
    private val SOF_REFUSED = setOf(0xC3, 0xC5, 0xC6, 0xC7, 0xC9, 0xCA, 0xCB, 0xCD, 0xCE, 0xCF)

    private fun parseJpeg(b: ImageBytes): ImageHeaderResult {
        var offset = 2L
        var segments = 0
        var frame: ImageHeader? = null
        var precision = 8
        var components = 3
        var inScan = false
        var seenScan = false
        val metadata = mutableListOf<ByteSpan>()
        while (true) {
            if (inScan) {
                while (offset < b.size && b.byteAt(offset) != 0xFF) offset++
            }
            if (offset >= b.size) return refused(ShotIssueCode.TRUNCATED, "This JPEG is cut off before its end-of-image marker.")
            if (b.byteAt(offset) != 0xFF) {
                return refused(ShotIssueCode.MALFORMED, "This JPEG has a stray byte where a marker belongs (byte $offset).")
            }
            var markerAt = offset + 1
            while (markerAt < b.size && b.byteAt(markerAt) == 0xFF) markerAt++
            if (markerAt >= b.size) return refused(ShotIssueCode.TRUNCATED, "This JPEG is cut off in a marker.")
            val marker = b.byteAt(markerAt)
            // FF00 is a literal FF entropy byte. Restart/TEM markers do not end a scan.
            if (inScan && (marker == 0x00 || marker in 0xD0..0xD7 || marker == 0x01)) {
                offset = markerAt + 1
                continue
            }
            inScan = false
            if (marker == 0xD9) {
                if (!seenScan) return refused(ShotIssueCode.MALFORMED, "This JPEG ends before its image data.")
                if (markerAt + 1 != b.size) {
                    return refused(ShotIssueCode.TRAILING_DATA, "This JPEG has ${b.size - markerAt - 1} bytes after its end. Re-export it.")
                }
                break
            }
            segments++
            if (segments > MAX_JPEG_SEGMENTS) return refused(ShotIssueCode.MALFORMED, "This JPEG has more than $MAX_JPEG_SEGMENTS segments.")
            if (marker in 0xD0..0xD7 || marker == 0x01) {
                offset = markerAt + 1
                continue
            }
            if (marker == 0x00 || marker == 0xD8) {
                return refused(ShotIssueCode.MALFORMED, "This JPEG has an unexpected marker (0x${hex(marker)}).")
            }
            if (markerAt + 2 >= b.size) return refused(ShotIssueCode.TRUNCATED, "This JPEG is cut off in a segment header.")
            val length = (b.byteAt(markerAt + 1) shl 8) or b.byteAt(markerAt + 2)
            if (length < 2) return refused(ShotIssueCode.MALFORMED, "This JPEG has a segment of impossible length.")
            val end = markerAt + 1 + length
            if (end > b.size) return refused(ShotIssueCode.TRUNCATED, "This JPEG is cut off in the middle of a segment.")
            when {
                marker in SOF_REFUSED -> return refused(
                    ShotIssueCode.UNSUPPORTED_TYPE,
                    "This is a lossless, hierarchical or arithmetic-coded JPEG. Save it as a baseline or progressive JPEG.",
                )
                marker in SOF_ACCEPTED -> {
                    if (frame != null) return refused(ShotIssueCode.MALFORMED, "This JPEG has two frame headers.")
                    if (length < 8) return refused(ShotIssueCode.MALFORMED, "This JPEG's frame header is too short.")
                    precision = b.byteAt(markerAt + 3)
                    val height = ((b.byteAt(markerAt + 4) shl 8) or b.byteAt(markerAt + 5)).toLong()
                    val width = ((b.byteAt(markerAt + 6) shl 8) or b.byteAt(markerAt + 7)).toLong()
                    components = b.byteAt(markerAt + 8)
                    if (length < 8 + 3 * components) return refused(ShotIssueCode.MALFORMED, "This JPEG's frame header is too short.")
                    if (width == 0L || height == 0L) return refused(ShotIssueCode.MALFORMED, "This JPEG says it is ${width}×$height pixels.")
                    dimensionIssue(width, height)?.let { return ImageHeaderResult.Refused(it) }
                    frame = ImageHeader(
                        format = ImageFormat.JPEG, width = width.toInt(), height = height.toInt(), hasAlpha = false,
                        bitDepth = precision, channels = components, interlacedOrProgressive = marker == 0xC2,
                    )
                }
                marker == 0xDA -> {
                    if (frame == null) return refused(ShotIssueCode.MALFORMED, "This JPEG starts its image data before its frame header.")
                    inScan = true
                    seenScan = true
                }
                marker == 0xE1 && (
                    startsWith(b, markerAt + 3, end, EXIF_ID) || startsWith(b, markerAt + 3, end, XMP_ID) ||
                        startsWith(b, markerAt + 3, end, EXTENDED_XMP_ID)
                ) ->
                    metadata += ByteSpan(offset, end - offset)
                marker == 0xED || marker == 0xFE || marker == 0xEB ||
                    (marker == 0xE0 && startsWith(b, markerAt + 3, end, JFXX_ID)) -> metadata += ByteSpan(offset, end - offset)
            }
            offset = end
        }
        if (precision != 8 || components != 3) {
            val what = when {
                precision != 8 -> "$precision-bit"
                components == 1 -> "greyscale"
                components == 4 -> "CMYK"
                else -> "$components-channel"
            }
            return refused(ShotIssueCode.COLOR_UNSUPPORTED, "This JPEG is $what. Save it as an 8-bit RGB JPEG.")
        }
        return ImageHeaderResult.Ok(frame!!, metadata)
    }

    private val JFXX_ID = "JFXX\u0000".encodeToByteArray()
    private val EXIF_ID = "Exif\u0000\u0000".encodeToByteArray()
    private val XMP_ID = "http://ns.adobe.com/xap/1.0/\u0000".encodeToByteArray()
    private val EXTENDED_XMP_ID = "http://ns.adobe.com/xmp/extension/\u0000".encodeToByteArray()

    private fun startsWith(b: ImageBytes, from: Long, end: Long, prefix: ByteArray): Boolean =
        from + prefix.size <= end && prefix.indices.all { b.byteAt(from + it) == (prefix[it].toInt() and 0xFF) }

    // --- shared ----------------------------------------------------------------------------------

    private fun dimensionIssue(width: Long, height: Long): ShotIssue? =
        if (width > ScreenshotSpecs.MAX_SIDE_PX || height > ScreenshotSpecs.MAX_SIDE_PX || width * height > ScreenshotSpecs.MAX_PIXELS) {
            ShotIssue(
                ShotIssueCode.DIMENSIONS_OUT_OF_RANGE,
                "This image says it is ${width}×$height pixels. No store accepts more than " +
                    "${ScreenshotSpecs.MAX_SIDE_PX} px on a side.",
            )
        } else {
            null
        }

    private fun u32(b: ImageBytes, offset: Long): Long =
        (b.byteAt(offset).toLong() shl 24) or (b.byteAt(offset + 1).toLong() shl 16) or
            (b.byteAt(offset + 2).toLong() shl 8) or b.byteAt(offset + 3).toLong()

    private fun refused(code: ShotIssueCode, message: String) = ImageHeaderResult.Refused(ShotIssue(code, message))

    private fun hex(value: Int): String = value.toString(16).uppercase().padStart(2, '0')

    /** Names the format a refused file most likely is, so the refusal is actionable. */
    private fun sniff(b: ImageBytes): String {
        fun ascii(from: Long, text: String) = b.size >= from + text.length &&
            text.indices.all { b.byteAt(from + it) == text[it].code }
        return when {
            b.size == 0L -> "This file is empty."
            ascii(0, "RIFF") && ascii(8, "WEBP") -> "This is a WebP image."
            ascii(0, "GIF8") -> "This is a GIF image."
            ascii(4, "ftyp") -> "This is a HEIC/AVIF image. Export it as PNG."
            b.byteAt(0) == '<'.code -> "This looks like SVG or XML, which can carry scripts."
            else -> "This file is neither."
        }
    }
}

/** CRC-32 (IEEE 802.3), table-driven, over a slice of [ImageBytes]. */
internal object Crc32 {
    private val TABLE = IntArray(256) { n ->
        var c = n
        repeat(8) { c = if (c and 1 != 0) (c ushr 1) xor 0xEDB88320.toInt() else c ushr 1 }
        c
    }

    fun of(b: ImageBytes, offset: Long, length: Int): Long {
        var crc = -1
        for (i in 0 until length) crc = TABLE[(crc xor b.byteAt(offset + i)) and 0xFF] xor (crc ushr 8)
        return (crc xor -1).toLong() and 0xFFFFFFFFL
    }

    fun of(bytes: ByteArray): Long = of(ByteArrayImageBytes(bytes), 0, bytes.size)
}
